import React, { useState, useEffect } from "react";
import Card from "components/card";
import Dropdown from "components/dropdown";
import { MdExpandMore, MdExpandLess, MdAdd, MdEdit, MdDelete, MdClose, MdKeyboardArrowDown } from "react-icons/md";
import { 
  FaPlane, 
  FaHotel, 
  FaTrain, 
  FaCar, 
  FaPassport,
  FaSpinner 
} from "react-icons/fa";
import FlightModal from "./components/FlightModal";
import HotelModal from "./components/HotelModal";
import RailwayModal from "./components/RailwayModal";
import CarModal from "./components/CarModal";
import VisaModal from "./components/VisaModal";
import DeleteModal from "./components/DeleteModal";
import { generateCustomerId, getNextCustomerId } from "utils/customerUtils";

const BookingDetails = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [bookingData, setBookingData] = useState({
    flight: [],
    hotel: [],
    railway: [],
    car: [],
    visa: []
  });
  const [modals, setModals] = useState({
    flight: false,
    hotel: false,
    railway: false,
    car: false,
    visa: false
  });
  const [editingItem, setEditingItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, cardId: null, itemId: null, itemName: '' });
  const [nextId, setNextId] = useState({
    flight: 4,
    hotel: 4,
    railway: 4,
    car: 4,
    visa: 4
  });
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [editingCustomer, setEditingCustomer] = useState(!new URLSearchParams(window.location.search).get('carteId'));
  const [staticItems, setStaticItems] = useState({
    flight: [],
    hotel: [],
    railway: [],
    car: [],
    visa: []
  });
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [cardsEnabled, setCardsEnabled] = useState(false);

  // Load carte data if carteId is provided in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const carteId = urlParams.get('carteId');
    
    if (carteId) {
      const savedCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
      const carteIndex = parseInt(carteId) - 1;
      
      if (savedCartes[carteIndex]) {
        const carteData = savedCartes[carteIndex];
        
        // Load customer data
        setCustomerData(carteData.customer);
        setSelectedEmployee(carteData.customer.name);
        setCardsEnabled(true);
        
        // Load booking data
        setStaticItems({
          flight: carteData.bookings.flight || [],
          hotel: carteData.bookings.hotel || [],
          railway: carteData.bookings.railway || [],
          car: carteData.bookings.car || [],
          visa: carteData.bookings.visa || []
        });
      }
    }
  }, []);

  // Auto-save functionality
  useEffect(() => {
    if (!selectedEmployee || !cardsEnabled) return;
    
    const hasBookings = Object.values(bookingData).some(items => items.length > 0) || 
                      Object.values(staticItems).some(items => items.length > 0);
    
    if (hasBookings) {
      setIsAutoSaving(true);
      
      const timer = setTimeout(() => {
        saveToLocalStorage();
        setIsAutoSaving(false);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [bookingData, staticItems, selectedEmployee, cardsEnabled]);

  const saveToLocalStorage = () => {
    const carteData = {
      customer: customerData,
      bookings: {
        flight: [...bookingData.flight, ...staticItems.flight],
        hotel: [...bookingData.hotel, ...staticItems.hotel],
        railway: [...bookingData.railway, ...staticItems.railway],
        car: [...bookingData.car, ...staticItems.car],
        visa: [...bookingData.visa, ...staticItems.visa]
      },
      timestamp: new Date().toISOString()
    };
    
    const existingCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
    const urlParams = new URLSearchParams(window.location.search);
    const carteId = urlParams.get('carteId');
    
    if (carteId) {
      // Update existing carte
      const carteIndex = parseInt(carteId) - 1;
      if (existingCartes[carteIndex]) {
        existingCartes[carteIndex] = carteData;
      }
    } else {
      // Check if customer already has a carte
      const existingIndex = existingCartes.findIndex(carte => 
        carte.customer.id === customerData.id
      );
      
      if (existingIndex !== -1) {
        // Update existing customer's carte
        existingCartes[existingIndex] = carteData;
      } else {
        // Create new carte
        existingCartes.unshift(carteData);
      }
    }
    
    localStorage.setItem('savedCartes', JSON.stringify(existingCartes));
    
    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('cartesUpdated'));
  };


  const toggleCard = (cardId) => {
    setExpandedCard(prev => prev === cardId ? null : cardId);
  };

  const openModal = (cardId) => {
    setModals(prev => ({ ...prev, [cardId]: true }));
  };

  const closeModal = (cardId) => {
    setModals(prev => ({ ...prev, [cardId]: false }));
  };

  const addItem = (cardId, item) => {
    if (editingItem) {
      // Update existing item
      if (editingItem.id <= 3) {
        setStaticItems(prev => ({
          ...prev,
          [cardId]: prev[cardId].map(existingItem => 
            existingItem.id === editingItem.id ? { ...item, id: editingItem.id } : existingItem
          )
        }));
      } else {
        setBookingData(prev => ({
          ...prev,
          [cardId]: prev[cardId].map(existingItem => 
            existingItem.id === editingItem.id ? { ...item, id: editingItem.id } : existingItem
          )
        }));
      }
      setEditingItem(null);
    } else {
      // Add new item
      const newItem = { ...item, id: nextId[cardId] };
      setBookingData(prev => ({
        ...prev,
        [cardId]: [...prev[cardId], newItem]
      }));
      setNextId(prev => ({ ...prev, [cardId]: prev[cardId] + 1 }));
    }
  };

  const openDeleteModal = (cardId, itemId, itemName) => {
    setDeleteModal({ isOpen: true, cardId, itemId, itemName });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, cardId: null, itemId: null, itemName: '' });
  };

  const confirmDelete = () => {
    const { cardId, itemId } = deleteModal;
    
    if (itemId <= 3) {
      // Remove from static items
      setStaticItems(prev => ({
        ...prev,
        [cardId]: prev[cardId].filter(item => item.id !== itemId)
      }));
    } else {
      // Remove from dynamic items
      setBookingData(prev => ({
        ...prev,
        [cardId]: prev[cardId].filter(item => item.id !== itemId)
      }));
    }
    
    closeDeleteModal();
  };

  const editItem = (cardId, item) => {
    setEditingItem(item);
    openModal(cardId);
  };





  // Sample customer data
  const sampleCustomers = [
    {
      id: "CUST001",
      name: "John Doe",
      email: "john.doe@email.com",
      phone: "+1-555-0123",
      address: "123 Main St, New York, NY 10001"
    },
    {
      id: "CUST002",
      name: "Jane Smith",
      email: "jane.smith@email.com",
      phone: "+1-555-0124",
      address: "456 Oak Ave, Los Angeles, CA 90210"
    },
    {
      id: "CUST003",
      name: "Mike Johnson",
      email: "mike.johnson@email.com",
      phone: "+1-555-0125",
      address: "789 Pine Rd, Chicago, IL 60601"
    },
    {
      id: "CUST004",
      name: "Sarah Wilson",
      email: "sarah.wilson@email.com",
      phone: "+1-555-0126",
      address: "321 Elm St, Houston, TX 77001"
    }
  ];

  const [customerData, setCustomerData] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const carteId = urlParams.get('carteId');
    
    if (carteId) {
      return {
        id: "CUST001",
        name: "",
        email: "",
        phone: "",
        address: ""
      };
    } else {
      // Generate new ID for new carte
      const savedCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
      const nextId = savedCartes.length + 1;
      return {
        id: `CUST${String(nextId).padStart(3, '0')}`,
        name: "",
        email: "",
        phone: "",
        address: ""
      };
    }
  });

  const [selectedCustomer, setSelectedCustomer] = useState("Select Customer");

  const handleCustomerEdit = (field, value) => {
    setCustomerData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerSelect = (customerName) => {
    if (customerName === "Select Customer" || customerName === "New Customer") {
      setSelectedCustomer(customerName);
      setSelectedEmployee("");
      setCardsEnabled(false);
      
      // Clear previous data
      setBookingData({
        flight: [],
        hotel: [],
        railway: [],
        car: [],
        visa: []
      });
      setStaticItems({
        flight: [],
        hotel: [],
        railway: [],
        car: [],
        visa: []
      });
      
      if (customerName === "New Customer") {
        const savedCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
        const nextId = savedCartes.length + 1;
        setCustomerData({
          id: `CUST${String(nextId).padStart(3, '0')}`,
          name: "",
          email: "",
          phone: "",
          address: ""
        });
      }
      return;
    }
    
    const customer = sampleCustomers.find(c => c.name === customerName);
    if (customer) {
      setSelectedCustomer(customerName);
      setSelectedEmployee(customerName);
      setCustomerData(customer);
      setCardsEnabled(true);
      
      // Load existing customer data if available
      const existingCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
      const existingCarte = existingCartes.find(carte => carte.customer.id === customer.id);
      
      if (existingCarte) {
        setStaticItems({
          flight: existingCarte.bookings.flight || [],
          hotel: existingCarte.bookings.hotel || [],
          railway: existingCarte.bookings.railway || [],
          car: existingCarte.bookings.car || [],
          visa: existingCarte.bookings.visa || []
        });
        setBookingData({
          flight: [],
          hotel: [],
          railway: [],
          car: [],
          visa: []
        });
      } else {
        // Clear data for new customer
        setBookingData({
          flight: [],
          hotel: [],
          railway: [],
          car: [],
          visa: []
        });
        setStaticItems({
          flight: [],
          hotel: [],
          railway: [],
          car: [],
          visa: []
        });
      }
    }
  };

  const bookingCards = [
    {
      id: "flight",
      title: "Available Flights",
      icon: <FaPlane className="h-5 w-5" />,
      color: "bg-blue-500",
      items: [...bookingData.flight, ...staticItems.flight]
    },
    {
      id: "hotel",
      title: "Available Hotels",
      icon: <FaHotel className="h-5 w-5" />,
      color: "bg-green-500",
      items: [...bookingData.hotel, ...staticItems.hotel]
    },
    {
      id: "railway",
      title: "Available Trains",
      icon: <FaTrain className="h-5 w-5" />,
      color: "bg-purple-500",
      items: [...bookingData.railway, ...staticItems.railway]
    },
    {
      id: "car",
      title: "Available Vehicles",
      icon: <FaCar className="h-5 w-5" />,
      color: "bg-orange-500",
      items: [...bookingData.car, ...staticItems.car]
    },
    {
      id: "visa",
      title: "Visa Services",
      icon: <FaPassport className="h-5 w-5" />,
      color: "bg-red-500",
      items: [...bookingData.visa, ...staticItems.visa]
    }
  ];

  return (
    <div className="mt-3">
      {/* Customer Header Section */}
      <Card extra="mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {new URLSearchParams(window.location.search).get('carteId') && (
              <button
                onClick={() => window.location.href = '/admin/carte-listing'}
                className="rounded-lg bg-gray-500 px-3 py-1 text-white hover:bg-gray-600 text-sm flex items-center space-x-1"
              >
                <span>←</span>
                <span>Back</span>
              </button>
            )}
            
          </div>
          <div className="flex items-center space-x-3">
            {new URLSearchParams(window.location.search).get('carteId') ? (
              <div className="rounded-lg bg-lightPrimary px-3 py-1 dark:bg-navy-700">
                <span className="text-sm font-medium text-brand-500 dark:text-white">
                  ID: {customerData.id}
                </span>
              </div>
            ) : (
              <div className="rounded-lg bg-green-100 px-3 py-1 dark:bg-green-900">
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  New Carte - ID: {customerData.id}
                </span>
              </div>
            )}

          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Customer Name</p>
            {!new URLSearchParams(window.location.search).get('carteId') ? (
              <div className="relative">
                <Dropdown
                  button={
                    <div className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-200 bg-white p-2 text-sm outline-none dark:border-white/10 dark:bg-navy-700 dark:text-white cursor-pointer hover:border-gray-300">
                      <span className={selectedCustomer === "Select Customer" ? "text-gray-400" : "text-navy-700 dark:text-white"}>
                        {selectedCustomer}
                      </span>
                      <MdKeyboardArrowDown className="h-4 w-4 text-gray-400" />
                    </div>
                  }
                  children={
                    <div className="flex w-56 flex-col justify-start rounded-[20px] bg-white bg-cover px-4 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleCustomerSelect("New Customer")}
                          className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-2 text-gray-600 hover:bg-gray-100 dark:text-white dark:hover:bg-white/20"
                        >
                          <div className="ml-2">
                            <p className="text-sm font-bold text-navy-700 dark:text-white">New Customer</p>
                          </div>
                        </button>
                        {sampleCustomers.map((customer) => (
                          <button
                            key={customer.id}
                            onClick={() => handleCustomerSelect(customer.name)}
                            className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-2 text-gray-600 hover:bg-gray-100 dark:text-white dark:hover:bg-white/20"
                          >
                            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                              {customer.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div className="ml-2">
                              <p className="text-sm font-bold text-navy-700 dark:text-white">{customer.name}</p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">{customer.id}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  }
                  classNames="py-2 top-8 -left-[180px] w-max"
                />
                {selectedCustomer === "New Customer" && (
                  <input
                    type="text"
                    value={customerData.name}
                    onChange={(e) => handleCustomerEdit('name', e.target.value)}
                    className="mt-2 text-lg font-semibold text-navy-700 dark:text-white bg-transparent border-b border-gray-300 focus:border-brand-500 focus:outline-none w-full"
                    placeholder="Enter customer name"
                  />
                )}
              </div>
            ) : editingCustomer ? (
              <input
                type="text"
                value={customerData.name}
                onChange={(e) => handleCustomerEdit('name', e.target.value)}
                className="text-lg font-semibold text-navy-700 dark:text-white bg-transparent border-b border-gray-300 focus:border-brand-500 focus:outline-none w-full"
                placeholder="Enter customer name"
              />
            ) : (
              <p className="text-lg font-semibold text-navy-700 dark:text-white">{customerData.name}</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Email</p>
            {editingCustomer || (!new URLSearchParams(window.location.search).get('carteId') && selectedCustomer === "New Customer") ? (
              <input
                type="email"
                value={customerData.email}
                onChange={(e) => handleCustomerEdit('email', e.target.value)}
                className="text-lg font-semibold text-navy-700 dark:text-white bg-transparent border-b border-gray-300 focus:border-brand-500 focus:outline-none w-full"
                placeholder="Enter email address"
              />
            ) : (
              <p className="text-lg font-semibold text-navy-700 dark:text-white">{customerData.email}</p>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Phone</p>
            {editingCustomer || (!new URLSearchParams(window.location.search).get('carteId') && selectedCustomer === "New Customer") ? (
              <input
                type="tel"
                value={customerData.phone}
                onChange={(e) => handleCustomerEdit('phone', e.target.value)}
                className="text-lg font-semibold text-navy-700 dark:text-white bg-transparent border-b border-gray-300 focus:border-brand-500 focus:outline-none w-full"
                placeholder="Enter phone number"
              />
            ) : (
              <p className="text-lg font-semibold text-navy-700 dark:text-white">{customerData.phone}</p>
            )}
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Address</p>
            {editingCustomer || (!new URLSearchParams(window.location.search).get('carteId') && selectedCustomer === "New Customer") ? (
              <input
                type="text"
                value={customerData.address}
                onChange={(e) => handleCustomerEdit('address', e.target.value)}
                className="text-lg font-semibold text-navy-700 dark:text-white bg-transparent border-b border-gray-300 focus:border-brand-500 focus:outline-none w-full"
                placeholder="Enter address"
              />
            ) : (
              <p className="text-lg font-semibold text-navy-700 dark:text-white">{customerData.address}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Auto-save indicator */}
      {isAutoSaving && (
        <div className="mb-6 flex items-center justify-center">
          <div className="flex items-center space-x-2 rounded-lg bg-green-100 px-4 py-2 text-green-700">
            <FaSpinner className="h-4 w-4 animate-spin" />
            <span className="text-sm font-medium">Auto-saving...</span>
          </div>
        </div>
      )}

      {/* Booking Cards Grid */}
      <div className="flex flex-wrap gap-5">
        {bookingCards.map((card) => (
          <div className="relative flex-1 min-w-0 md:flex-none md:w-[calc(50%-10px)]">
            <Card extra={`p-0 overflow-visible ${!cardsEnabled ? 'opacity-50' : ''}`}>
              <div
                className={`flex items-center justify-between p-4 ${
                  cardsEnabled 
                    ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700' 
                    : 'cursor-not-allowed'
                }`}
                onClick={() => cardsEnabled && toggleCard(card.id)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`rounded-full p-3 ${card.color}`}>
                    <span className="flex items-center text-white">
                      {card.icon}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                    {card.title}
                  </h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (cardsEnabled) openModal(card.id);
                    }}
                    disabled={!cardsEnabled}
                    className={`ml-2 rounded-full p-1 ${
                      cardsEnabled 
                        ? 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 cursor-pointer' 
                        : 'bg-gray-100 cursor-not-allowed'
                    }`}
                  >
                    <MdAdd className={`h-4 w-4 ${
                      cardsEnabled 
                        ? 'text-gray-600 dark:text-gray-300' 
                        : 'text-gray-400'
                    }`} />
                  </button>
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  {expandedCard === card.id ? (
                    <MdExpandLess className="h-6 w-6" />
                  ) : (
                    <MdExpandMore className="h-6 w-6" />
                  )}
                </div>
              </div>
            </Card>
            
            {expandedCard === card.id && cardsEnabled && (
              <div className="absolute top-full left-0 right-0 z-10 mt-2">
                <Card extra="p-4">
                  <div className="space-y-3">
                    {card.items.map((item, index) => {
                      const getItemStyle = () => {
                        switch(card.id) {
                          case 'flight':
                            return 'border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20';
                          case 'hotel':
                            return 'border-l-4 border-green-500 bg-green-50 dark:bg-green-900/20';
                          case 'railway':
                            return 'border-l-4 border-purple-500 bg-purple-50 dark:bg-purple-900/20';
                          case 'car':
                            return 'border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20';
                          case 'visa':
                            return 'border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20';
                          default:
                            return 'border-l-4 border-gray-500 bg-gray-50 dark:bg-gray-900/20';
                        }
                      };
                      
                      return (
                        <div key={index} className={`rounded-lg p-3 ${getItemStyle()} relative group`}>
                          <div className="mb-2">
                            <h4 className="font-semibold text-navy-700 dark:text-white pr-16">{item.name}</h4>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {Object.entries(item).filter(([key]) => key !== 'name' && key !== 'id').map(([key, value]) => (
                              <div key={key}>
                                <span className="text-gray-600 dark:text-gray-400 capitalize">{key}: </span>
                                <span className="font-medium text-navy-700 dark:text-white">{value}</span>
                              </div>
                            ))}
                          </div>
                          <div className="absolute bottom-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => editItem(card.id, item)}
                              className="rounded-full bg-blue-500 p-1 text-white hover:bg-blue-600 transition-colors"
                            >
                              <MdEdit className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => openDeleteModal(card.id, item.id, item.name)}
                              className="rounded-full bg-red-500 p-1 text-white hover:bg-red-600 transition-colors"
                            >
                              <MdDelete className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modals */}
      <FlightModal 
        isOpen={modals.flight} 
        onClose={() => { closeModal('flight'); setEditingItem(null); }} 
        onAdd={(item) => addItem('flight', item)}
        editingItem={editingItem}
      />
      <HotelModal 
        isOpen={modals.hotel} 
        onClose={() => { closeModal('hotel'); setEditingItem(null); }} 
        onAdd={(item) => addItem('hotel', item)}
        editingItem={editingItem}
      />
      <RailwayModal 
        isOpen={modals.railway} 
        onClose={() => { closeModal('railway'); setEditingItem(null); }} 
        onAdd={(item) => addItem('railway', item)}
        editingItem={editingItem}
      />
      <CarModal 
        isOpen={modals.car} 
        onClose={() => { closeModal('car'); setEditingItem(null); }} 
        onAdd={(item) => addItem('car', item)}
        editingItem={editingItem}
      />
      <VisaModal 
        isOpen={modals.visa} 
        onClose={() => { closeModal('visa'); setEditingItem(null); }} 
        onAdd={(item) => addItem('visa', item)}
        editingItem={editingItem}
      />
      
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        itemName={deleteModal.itemName}
      />
      

      
      {/* Alert Modal */}
      {alertModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <Card extra="w-96">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-t-[20px]">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">{alertModal.title}</h3>
                <button onClick={() => setAlertModal({ isOpen: false, title: '', message: '' })} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30">
                  <MdClose className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-6">{alertModal.message}</p>
              
              <button
                onClick={() => setAlertModal({ isOpen: false, title: '', message: '' })}
                className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 py-3 px-6 text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
              >
                OK
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BookingDetails;